package com.mps.asc_app.ui.screens

import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.dp
import com.mps.asc_app.data.user.UserRepository
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.functions.functions
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

private const val REQUEST_TARGET_EMAIL = "padel-registrierung@asccranz-estebruegge.de"
private const val ASC_MEMBERSHIP_PDF_URL = "/forms/aufnahme_antrag_2026.pdf"
private const val PADEL_MEMBERSHIP_PDF_URL = "/forms/beitritt_padel.pdf"
private const val PRIVACY_POLICY_URL = "/datenschutz"

private data class MembershipPrefill(
    val firstName: String,
    val lastName: String,
    val email: String,
    val phone: String,
    val city: String,
    val street: String,
    val houseNumber: String,
    val formattedAddress: String,
) {
    val fullName: String
        get() = listOf(firstName, lastName).filter { it.isNotBlank() }.joinToString(" ").trim()
}

@Composable
fun AscMembershipFormScreen(
    contentPadding: PaddingValues = PaddingValues(0.dp),
    onSubmitted: (() -> Unit)? = null,
    onBack: () -> Unit,
) {
    val userRepository = koinInject<UserRepository>()
    var familyAlreadyMember by remember { mutableStateOf("") }
    var existingMemberNumber by remember { mutableStateOf("") }
    var name by remember { mutableStateOf("") }
    var firstName by remember { mutableStateOf("") }
    var city by remember { mutableStateOf("") }
    var gender by remember { mutableStateOf("") }
    var birthDate by remember { mutableStateOf("") }
    var street by remember { mutableStateOf("") }
    var houseNumber by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var selectedSports by remember { mutableStateOf(setOf<String>()) }
    var additionalSport by remember { mutableStateOf("") }
    var activePassive by remember { mutableStateOf("") }
    var lastClub by remember { mutableStateOf("") }
    var leftAt by remember { mutableStateOf("") }
    var sepaStartAt by remember { mutableStateOf("") }
    var accountChanged by remember { mutableStateOf("") }
    var accountName by remember { mutableStateOf("") }
    var accountFirstName by remember { mutableStateOf("") }
    var accountBirthDate by remember { mutableStateOf("") }
    var accountCity by remember { mutableStateOf("") }
    var accountStreet by remember { mutableStateOf("") }
    var accountHouseNumber by remember { mutableStateOf("") }
    var bank by remember { mutableStateOf("") }
    var iban by remember { mutableStateOf("") }
    var bic by remember { mutableStateOf("") }
    var memberNamesForSepa by remember { mutableStateOf("") }
    var placeDate by remember { mutableStateOf("") }
    var note by remember { mutableStateOf("") }
    var acceptedRules by remember { mutableStateOf(false) }
    var acceptedDataProcessing by remember { mutableStateOf(false) }
    var acceptedEmailTransfer by remember { mutableStateOf(false) }
    var acceptedGuardianIfMinor by remember { mutableStateOf(false) }
    val uriHandler = LocalUriHandler.current
    val scope = rememberCoroutineScope()
    var isSubmitting by remember { mutableStateOf(false) }
    var submitSuccess by remember { mutableStateOf<String?>(null) }
    var submitError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        loadMembershipPrefill(userRepository).onSuccess { prefill ->
            if (name.isBlank()) name = prefill.lastName
            if (firstName.isBlank()) firstName = prefill.firstName
            if (email.isBlank()) email = prefill.email
            if (phone.isBlank()) phone = prefill.phone
            if (city.isBlank()) city = prefill.city
            if (street.isBlank()) street = prefill.street
            if (houseNumber.isBlank()) houseNumber = prefill.houseNumber
        }
    }

    val canSend = name.trim().isNotEmpty() &&
        firstName.trim().isNotEmpty() &&
        birthDate.trim().isNotEmpty() &&
        street.trim().isNotEmpty() &&
        city.trim().isNotEmpty() &&
        email.trim().isNotEmpty() &&
        (selectedSports.isNotEmpty() || additionalSport.trim().isNotEmpty()) &&
        acceptedRules &&
        acceptedDataProcessing &&
        acceptedEmailTransfer &&
        acceptedGuardianIfMinor &&
        !isSubmitting

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(contentPadding)
            .padding(16.dp)
            .verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Text("ASC-Mitgliedschaft anfragen", style = MaterialTheme.typography.headlineSmall)
        Text(
            "Online-Erfassung analog zum Aufnahmeantrag (inkl. SEPA-Bereich).",
            style = MaterialTheme.typography.bodyMedium
        )

        Card(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(12.dp),
                verticalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                Text("Beiträge (pro Quartal)", style = MaterialTheme.typography.titleSmall)
                Text(
                    "Familie: 66 € | Erwachsene: 33 € | Jugendliche/Kinder: 24 € | Passiv: 3 € | Spielgemeinschaft: 20 €",
                    style = MaterialTheme.typography.bodySmall
                )
                Text("Zusatz: Tennis/Padel 12 € | Padel 6 €", style = MaterialTheme.typography.bodySmall)
            }
        }

        Card(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(12.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                OutlinedTextField(
                    value = familyAlreadyMember,
                    onValueChange = { familyAlreadyMember = it },
                    label = { Text("Familienangehörige bereits Mitglied? (ja/nein)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = existingMemberNumber,
                    onValueChange = { existingMemberNumber = it },
                    label = { Text("Mitgliedsnummer (falls vorhanden)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text("Name") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = firstName,
                    onValueChange = { firstName = it },
                    label = { Text("Vorname") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = city,
                    onValueChange = { city = it },
                    label = { Text("Wohnort") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                Text("Geschlecht", style = MaterialTheme.typography.bodyMedium)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    listOf("weiblich", "männlich", "divers").forEach { option ->
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.weight(1f)
                        ) {
                            RadioButton(
                                selected = gender == option,
                                onClick = { gender = option }
                            )
                            Text(option)
                        }
                    }
                }
                DatePickerField(
                    label = "Geburtsdatum",
                    value = birthDate,
                    onValueChange = { birthDate = it }
                )
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    OutlinedTextField(
                        value = street,
                        onValueChange = { street = it },
                        label = { Text("Straße") },
                        modifier = Modifier.weight(1f),
                        singleLine = true
                    )
                    OutlinedTextField(
                        value = houseNumber,
                        onValueChange = { houseNumber = it },
                        label = { Text("Nr.") },
                        modifier = Modifier.weight(0.45f),
                        singleLine = true
                    )
                }
                OutlinedTextField(
                    value = phone,
                    onValueChange = { phone = it },
                    label = { Text("Telefon") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = email,
                    onValueChange = { email = it },
                    label = { Text("E-Mail") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                Text("Sportarten", style = MaterialTheme.typography.bodyMedium)
                listOf(
                    "Tennis",
                    "Padel",
                    "Fußball",
                    "Turnen",
                    "Volleyball",
                    "Fitness",
                    "Badminton"
                ).chunked(2).forEach { sportRow ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        sportRow.forEach { sport ->
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.weight(1f)
                            ) {
                                Checkbox(
                                    checked = selectedSports.contains(sport),
                                    onCheckedChange = { checked ->
                                        selectedSports = if (checked) {
                                            selectedSports + sport
                                        } else {
                                            selectedSports - sport
                                        }
                                    }
                                )
                                Text(sport)
                            }
                        }
                        if (sportRow.size == 1) {
                            Row(modifier = Modifier.weight(1f)) {}
                        }
                    }
                }
                OutlinedTextField(
                    value = additionalSport,
                    onValueChange = { additionalSport = it },
                    label = { Text("Weitere Sportart (optional)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = activePassive,
                    onValueChange = { activePassive = it },
                    label = { Text("Aktiv/Passiv") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = lastClub,
                    onValueChange = { lastClub = it },
                    label = { Text("Letzter Sportverein") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = leftAt,
                    onValueChange = { leftAt = it },
                    label = { Text("Ausgetreten am") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = note,
                    onValueChange = { note = it },
                    label = { Text("Hinweis (optional)") },
                    modifier = Modifier.fillMaxWidth(),
                    minLines = 3
                )

                Text("SEPA-Lastschriftmandat", style = MaterialTheme.typography.titleSmall)
                OutlinedTextField(
                    value = sepaStartAt,
                    onValueChange = { sepaStartAt = it },
                    label = { Text("Lastschrift ab (Datum)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = accountChanged,
                    onValueChange = { accountChanged = it },
                    label = { Text("Kontoänderung? (ja/nein)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = accountName,
                    onValueChange = { accountName = it },
                    label = { Text("Kontoinhaber Name") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = accountFirstName,
                    onValueChange = { accountFirstName = it },
                    label = { Text("Kontoinhaber Vorname") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                DatePickerField(
                    label = "Kontoinhaber Geburtsdatum",
                    value = accountBirthDate,
                    onValueChange = { accountBirthDate = it }
                )
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    OutlinedTextField(
                        value = accountCity,
                        onValueChange = { accountCity = it },
                        label = { Text("Kontoinhaber Wohnort") },
                        modifier = Modifier.weight(1f),
                        singleLine = true
                    )
                    OutlinedTextField(
                        value = accountStreet,
                        onValueChange = { accountStreet = it },
                        label = { Text("Straße") },
                        modifier = Modifier.weight(1f),
                        singleLine = true
                    )
                    OutlinedTextField(
                        value = accountHouseNumber,
                        onValueChange = { accountHouseNumber = it },
                        label = { Text("Nr.") },
                        modifier = Modifier.weight(0.4f),
                        singleLine = true
                    )
                }
                OutlinedTextField(
                    value = bank,
                    onValueChange = { bank = it },
                    label = { Text("Bank") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = iban,
                    onValueChange = { iban = it },
                    label = { Text("IBAN") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = bic,
                    onValueChange = { bic = it },
                    label = { Text("BIC") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = memberNamesForSepa,
                    onValueChange = { memberNamesForSepa = it },
                    label = { Text("Name/n des/r Mitglied/es/er") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = placeDate,
                    onValueChange = { placeDate = it },
                    label = { Text("Ort, Datum") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    Checkbox(checked = acceptedRules, onCheckedChange = { acceptedRules = it })
                    Text(
                        "Ich erkenne Satzung, Abteilungsordnung und Beitragsregelung an.",
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.weight(1f)
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    Checkbox(checked = acceptedDataProcessing, onCheckedChange = { acceptedDataProcessing = it })
                    Text(
                        "Ich willige ein, dass meine Angaben zur Bearbeitung meiner Anfrage verarbeitet werden.",
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.weight(1f)
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    Checkbox(checked = acceptedEmailTransfer, onCheckedChange = { acceptedEmailTransfer = it })
                    Text(
                        "Mir ist bekannt, dass die Anfrage per E-Mail an $REQUEST_TARGET_EMAIL übermittelt wird.",
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.weight(1f)
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    Checkbox(checked = acceptedGuardianIfMinor, onCheckedChange = { acceptedGuardianIfMinor = it })
                    Text(
                        "Falls ich minderjährig bin, liegt die Zustimmung eines Erziehungsberechtigten vor.",
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.weight(1f)
                    )
                }
                OutlinedButton(
                    onClick = { uriHandler.openUri(PRIVACY_POLICY_URL) },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text("Datenschutzhinweise öffnen")
                }
            }
        }

        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            Button(
                onClick = {
                    val sports = buildList {
                        addAll(selectedSports.sorted())
                        val extra = additionalSport.trim()
                        if (extra.isNotEmpty()) add(extra)
                    }.joinToString(", ")
                    val body = buildString {
                        appendLine("Aufnahmeantrag ASC")
                        appendLine()
                        appendLine("Familienangehörige bereits Mitglied: ${familyAlreadyMember.trim()}")
                        appendLine("Mitgliedsnummer (falls vorhanden): ${existingMemberNumber.trim()}")
                        appendLine("Name: ${name.trim()}")
                        appendLine("Vorname: ${firstName.trim()}")
                        appendLine("Wohnort: ${city.trim()}")
                        appendLine("Geschlecht: ${gender.trim()}")
                        appendLine("Geburtsdatum: ${birthDate.trim()}")
                        appendLine("Straße: ${street.trim()} ${houseNumber.trim()}")
                        appendLine("Telefon: ${phone.trim()}")
                        appendLine("E-Mail: ${email.trim()}")
                        appendLine("Sportarten: $sports")
                        appendLine("Aktiv/Passiv: ${activePassive.trim()}")
                        appendLine("Letzter Sportverein: ${lastClub.trim()}")
                        appendLine("Ausgetreten am: ${leftAt.trim()}")
                        appendLine("Hinweis: ${note.trim()}")
                        appendLine()
                        appendLine("SEPA")
                        appendLine("Lastschrift ab: ${sepaStartAt.trim()}")
                        appendLine("Kontoänderung: ${accountChanged.trim()}")
                        appendLine("Kontoinhaber Name: ${accountName.trim()}")
                        appendLine("Kontoinhaber Vorname: ${accountFirstName.trim()}")
                        appendLine("Kontoinhaber Geburtsdatum: ${accountBirthDate.trim()}")
                        appendLine("Kontoinhaber Wohnort: ${accountCity.trim()}")
                        appendLine("Kontoinhaber Straße/Nr: ${accountStreet.trim()} ${accountHouseNumber.trim()}")
                        appendLine("Bank: ${bank.trim()}")
                        appendLine("IBAN: ${iban.trim()}")
                        appendLine("BIC: ${bic.trim()}")
                        appendLine("Name/n des/r Mitglied/es/er: ${memberNamesForSepa.trim()}")
                        appendLine("Ort, Datum: ${placeDate.trim()}")
                        appendLine("Einwilligung Datenverarbeitung: ja")
                        appendLine("Hinweis E-Mail-Übertragung akzeptiert: ja")
                        appendLine("Minderjährige: Zustimmung Erziehungsberechtigte liegt vor: ja")
                    }
                    submitSuccess = null
                    submitError = null
                    isSubmitting = true
                    scope.launch {
                        sendMembershipRequestMail(
                            subject = "Aufnahmeantrag ASC",
                            body = body,
                        ).onSuccess {
                            submitSuccess = "Antrag wurde direkt versendet."
                            onSubmitted?.invoke()
                        }.onFailure { err ->
                            submitError = err.message ?: "Versand fehlgeschlagen."
                        }
                        isSubmitting = false
                    }
                },
                enabled = canSend,
                modifier = Modifier.weight(1f)
            ) {
                if (isSubmitting) {
                    CircularProgressIndicator(strokeWidth = 2.dp)
                } else {
                    Text("Direkt senden")
                }
            }
            OutlinedButton(
                onClick = { uriHandler.openUri(ASC_MEMBERSHIP_PDF_URL) },
                modifier = Modifier.weight(1f)
            ) {
                Text("PDF öffnen")
            }
        }

        submitSuccess?.let {
            Text(it, color = MaterialTheme.colorScheme.primary)
        }
        submitError?.let {
            Text(it, color = MaterialTheme.colorScheme.error)
        }

        OutlinedButton(
            onClick = onBack,
            modifier = Modifier.fillMaxWidth()
        ) {
            Text("Zurück")
        }
    }
}

@Composable
fun PadelMembershipFormScreen(
    contentPadding: PaddingValues = PaddingValues(0.dp),
    onSubmitted: (() -> Unit)? = null,
    onBack: () -> Unit,
) {
    val userRepository = koinInject<UserRepository>()
    var name by remember { mutableStateOf("") }
    var birthDate by remember { mutableStateOf("") }
    var address by remember { mutableStateOf("") }
    var contact by remember { mutableStateOf("") }
    var joinFrom by remember { mutableStateOf("") }
    var placeDate by remember { mutableStateOf("") }
    var note by remember { mutableStateOf("") }
    var acceptedTerms by remember { mutableStateOf(false) }
    var acceptedDataProcessing by remember { mutableStateOf(false) }
    var acceptedEmailTransfer by remember { mutableStateOf(false) }
    var acceptedGuardianIfMinor by remember { mutableStateOf(false) }
    val uriHandler = LocalUriHandler.current
    val scope = rememberCoroutineScope()
    var isSubmitting by remember { mutableStateOf(false) }
    var submitSuccess by remember { mutableStateOf<String?>(null) }
    var submitError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        loadMembershipPrefill(userRepository).onSuccess { prefill ->
            if (name.isBlank()) name = prefill.fullName
            if (address.isBlank()) {
                val fallbackAddress = listOf(prefill.street, prefill.houseNumber, prefill.city)
                    .filter { it.isNotBlank() }
                    .joinToString(", ")
                address = prefill.formattedAddress.ifBlank { fallbackAddress }
            }
            if (contact.isBlank()) {
                val contactValue = listOf(prefill.phone, prefill.email)
                    .filter { it.isNotBlank() }
                    .joinToString(" / ")
                contact = contactValue
            }
        }
    }

    val canSend = name.trim().isNotEmpty() &&
        birthDate.trim().isNotEmpty() &&
        address.trim().isNotEmpty() &&
        contact.trim().isNotEmpty() &&
        joinFrom.trim().isNotEmpty() &&
        acceptedTerms &&
        acceptedDataProcessing &&
        acceptedEmailTransfer &&
        acceptedGuardianIfMinor &&
        !isSubmitting

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(contentPadding)
            .padding(16.dp)
            .verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Text("Padel-Abteilung anfragen", style = MaterialTheme.typography.headlineSmall)
        Text(
            "Online-Erfassung analog zur Beitrittserklärung Sparte Padel.",
            style = MaterialTheme.typography.bodyMedium
        )

        Card(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(12.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text("Name") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                DatePickerField(
                    label = "Geburtsdatum",
                    value = birthDate,
                    onValueChange = { birthDate = it }
                )
                OutlinedTextField(
                    value = address,
                    onValueChange = { address = it },
                    label = { Text("Anschrift") },
                    modifier = Modifier.fillMaxWidth(),
                    minLines = 2
                )
                OutlinedTextField(
                    value = contact,
                    onValueChange = { contact = it },
                    label = { Text("Telefon / E-Mail") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = joinFrom,
                    onValueChange = { joinFrom = it },
                    label = { Text("Beitritt zur Sparte Padel ab dem") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = placeDate,
                    onValueChange = { placeDate = it },
                    label = { Text("Ort, Datum") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = note,
                    onValueChange = { note = it },
                    label = { Text("Hinweis (optional)") },
                    modifier = Modifier.fillMaxWidth(),
                    minLines = 3
                )
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    Checkbox(checked = acceptedTerms, onCheckedChange = { acceptedTerms = it })
                    Text(
                        "Ich erkenne Satzung, Ordnungen und Beiträge (inkl. Padel-Zusatzbeitrag) an.",
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.weight(1f)
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    Checkbox(checked = acceptedDataProcessing, onCheckedChange = { acceptedDataProcessing = it })
                    Text(
                        "Ich willige ein, dass meine Angaben zur Bearbeitung meiner Anfrage verarbeitet werden.",
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.weight(1f)
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    Checkbox(checked = acceptedEmailTransfer, onCheckedChange = { acceptedEmailTransfer = it })
                    Text(
                        "Mir ist bekannt, dass die Anfrage per E-Mail an $REQUEST_TARGET_EMAIL übermittelt wird.",
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.weight(1f)
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    Checkbox(checked = acceptedGuardianIfMinor, onCheckedChange = { acceptedGuardianIfMinor = it })
                    Text(
                        "Falls ich minderjährig bin, liegt die Zustimmung eines Erziehungsberechtigten vor.",
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.weight(1f)
                    )
                }
                OutlinedButton(
                    onClick = { uriHandler.openUri(PRIVACY_POLICY_URL) },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text("Datenschutzhinweise öffnen")
                }
            }
        }

        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            Button(
                onClick = {
                    val body = buildString {
                        appendLine("Beitrittserklärung Sparte Padel")
                        appendLine()
                        appendLine("Name: ${name.trim()}")
                        appendLine("Geburtsdatum: ${birthDate.trim()}")
                        appendLine("Anschrift: ${address.trim()}")
                        appendLine("Telefon / E-Mail: ${contact.trim()}")
                        appendLine("Beitritt ab: ${joinFrom.trim()}")
                        appendLine("Ort, Datum: ${placeDate.trim()}")
                        appendLine("Hinweis: ${note.trim()}")
                        appendLine("Einwilligung Datenverarbeitung: ja")
                        appendLine("Hinweis E-Mail-Übertragung akzeptiert: ja")
                        appendLine("Minderjährige: Zustimmung Erziehungsberechtigte liegt vor: ja")
                    }
                    submitSuccess = null
                    submitError = null
                    isSubmitting = true
                    scope.launch {
                        sendMembershipRequestMail(
                            subject = "Beitrittserklärung Padel",
                            body = body,
                        ).onSuccess {
                            submitSuccess = "Antrag wurde direkt versendet."
                            onSubmitted?.invoke()
                        }.onFailure { err ->
                            submitError = err.message ?: "Versand fehlgeschlagen."
                        }
                        isSubmitting = false
                    }
                },
                enabled = canSend,
                modifier = Modifier.weight(1f)
            ) {
                if (isSubmitting) {
                    CircularProgressIndicator(strokeWidth = 2.dp)
                } else {
                    Text("Direkt senden")
                }
            }
            OutlinedButton(
                onClick = { uriHandler.openUri(PADEL_MEMBERSHIP_PDF_URL) },
                modifier = Modifier.weight(1f)
            ) {
                Text("PDF öffnen")
            }
        }

        submitSuccess?.let {
            Text(it, color = MaterialTheme.colorScheme.primary)
        }
        submitError?.let {
            Text(it, color = MaterialTheme.colorScheme.error)
        }

        OutlinedButton(
            onClick = onBack,
            modifier = Modifier.fillMaxWidth()
        ) {
            Text("Zurück")
        }
    }
}

private suspend fun sendMembershipRequestMail(
    subject: String,
    body: String,
): Result<Unit> = runCatching {
    Firebase.functions(region = "europe-west1")
        .httpsCallable("sendMembershipRequestMail")(
            mapOf(
                "subject" to subject,
                "body" to body,
            )
        )
}.map { }

private suspend fun loadMembershipPrefill(
    userRepository: UserRepository,
): Result<MembershipPrefill> = runCatching {
    val currentUser = Firebase.auth.currentUser ?: error("Nicht eingeloggt.")
    val uid = currentUser.uid
    val profile = userRepository.getMemberProfile(uid)

    val firstName = profile.firstName.trim()
    val lastName = profile.lastName.trim()
    val email = profile.email.trim().ifBlank { currentUser.email.orEmpty().trim() }
    val phone = profile.phone.trim()

    val plainAddress = profile.addressText.trim()
    val formattedAddress = plainAddress
    val streetWithNumber = plainAddress.substringBefore(",").trim()
    val fallbackCity = plainAddress.substringAfter(",", "").trim()

    val streetSplit = splitStreetAndHouseNumber(streetWithNumber)
    MembershipPrefill(
        firstName = firstName,
        lastName = lastName,
        email = email,
        phone = phone,
        city = fallbackCity,
        street = streetSplit.first,
        houseNumber = streetSplit.second,
        formattedAddress = formattedAddress,
    )
}

private fun splitStreetAndHouseNumber(value: String): Pair<String, String> {
    val trimmed = value.trim()
    if (trimmed.isBlank()) return "" to ""

    val match = Regex("^(.*?)(\\d+[\\w\\-/]*)$").find(trimmed)
    if (match != null) {
        val street = match.groupValues[1].trim()
        val number = match.groupValues[2].trim()
        return street to number
    }
    return trimmed to ""
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DatePickerField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
) {
    var showDialog by remember { mutableStateOf(false) }

    OutlinedTextField(
        value = value,
        onValueChange = {},
        label = { Text(label) },
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        readOnly = true
    )

    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
        OutlinedButton(
            onClick = { showDialog = true },
            modifier = Modifier.weight(1f)
        ) {
            Text("Datum wählen")
        }
        if (value.isNotBlank()) {
            OutlinedButton(
                onClick = { onValueChange("") },
                modifier = Modifier.weight(1f)
            ) {
                Text("Löschen")
            }
        }
    }

    if (showDialog) {
        val dateState = rememberDatePickerState()
        DatePickerDialog(
            onDismissRequest = { showDialog = false },
            confirmButton = {
                TextButton(
                    onClick = {
                        val selected = dateState.selectedDateMillis
                        if (selected != null) {
                            onValueChange(epochMillisToDateString(selected))
                        }
                        showDialog = false
                    }
                ) {
                    Text("Übernehmen")
                }
            },
            dismissButton = {
                TextButton(onClick = { showDialog = false }) {
                    Text("Abbrechen")
                }
            }
        ) {
            DatePicker(state = dateState)
        }
    }
}

@OptIn(kotlin.time.ExperimentalTime::class)
private fun epochMillisToDateString(epochMillis: Long): String {
    return runCatching {
        Instant.fromEpochMilliseconds(epochMillis)
            .toLocalDateTime(TimeZone.currentSystemDefault())
            .date
            .toString()
    }.getOrElse {
        ""
    }
}
